import React, { useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import MABranchVisualization from "./MABranchVisualization";
import MABranchSubTable from "./MABranchSubTable";
import {
  INITIAL_ANIMATION_DELAY_MS,
  TRANSLATION_Y_OFFSET,
} from "./MABranchConstants";

/**
 * Compute how far left the cusip column is (px) and its width
 * so we can position the branch visualization + subtable correctly.
 */
const computeCusipMetrics = (cols, widths) => {
  if (!Array.isArray(cols)) return null;
  const cusipIndex = cols.findIndex((col) => col.key === "cusip");
  if (cusipIndex < 0) return null;
  const cusipWidth = widths?.cusip ?? 150;
  const leftBeforeCusip = cols
    .slice(0, cusipIndex)
    .reduce((acc, col) => acc + (widths?.[col.key] ?? 150), 0);
  return { leftBeforeCusip, cusipWidth };
};

/**
 * Compute the left offset for each subtable column so it aligns
 * underneath the corresponding main table column.
 * Returns the left-px of the first sub-column (bidDealer) relative
 * to the start of the branch container (i.e. relative to `ml`).
 */
const computeSubTableLeftOffset = (cols, widths) => {
  if (!Array.isArray(cols)) return 0;
  const bidDealerIndex = cols.findIndex((col) => col.key === "bidDealer");
  if (bidDealerIndex < 0) return 0;
  return cols
    .slice(0, bidDealerIndex)
    .reduce((acc, col) => acc + (widths?.[col.key] ?? 150), 0);
};

/**
 * MABranchTable — orchestrator for the cusip-based MA branch expansion.
 * Mirrors the architecture of RfqBranchTable but is much simpler since
 * there is no API interaction, just dummy data rendered visually.
 *
 * Props
 * -----
 * @param {Object}  parentRowData   - the main table row that was expanded
 * @param {Object}  parentCellRef   - React ref to the cusip TableCell
 * @param {Object}  columnWidths    - main table columnWidths state
 * @param {Array}   colsBondTable   - main table columns array (ordered)
 * @param {Array}   dealers         - array of dealer sub-row objects for this cusip
 */
const MABranchTable = ({
  parentRowData,
  parentCellRef,
  columnWidths,
  colsBondTable,
  dealers = [],
}) => {
  const resolveEffectiveColumnWidths = () => {
    const tableElement = parentCellRef?.current?.closest("table");
    if (!tableElement) return columnWidths || {};

    const headerRow = tableElement.querySelector("thead tr");
    if (!headerRow) return columnWidths || {};

    const headerCells = Array.from(headerRow.children).filter(
      (cell) => cell.tagName === "TH"
    );

    if (!Array.isArray(colsBondTable) || headerCells.length !== colsBondTable.length) {
      return columnWidths || {};
    }

    const measured = {};
    colsBondTable.forEach((col, index) => {
      const width = headerCells[index]?.getBoundingClientRect?.().width;
      if (Number.isFinite(width) && width > 0) {
        measured[col.key] = width;
      }
    });

    return Object.keys(measured).length ? measured : (columnWidths || {});
  };

  const [effectiveColumnWidths, setEffectiveColumnWidths] = useState(() =>
    resolveEffectiveColumnWidths()
  );

  useEffect(() => {
    const updateWidths = () => {
      setEffectiveColumnWidths(resolveEffectiveColumnWidths());
    };

    updateWidths();
    window.addEventListener("resize", updateWidths);
    const tableElement = parentCellRef?.current?.closest("table");
    const headerRow = tableElement?.querySelector("thead tr");
    let resizeObserver;

    if (typeof ResizeObserver !== "undefined" && tableElement) {
      resizeObserver = new ResizeObserver(() => updateWidths());
      resizeObserver.observe(tableElement);
      if (headerRow) {
        resizeObserver.observe(headerRow);
      }
    }

    return () => {
      window.removeEventListener("resize", updateWidths);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [columnWidths, colsBondTable, parentCellRef]);
  // Determine the cusip column width so we can size the branch viz
  const getCusipWidth = () => {
    const metrics = computeCusipMetrics(colsBondTable, effectiveColumnWidths);
    return metrics ? metrics.cusipWidth : 150;
  };

  const branchWidth = getCusipWidth();
  const branchMidpoint = `${branchWidth / 2}px`;

  const resolveInitialTableLeft = () => {
    const metrics = computeCusipMetrics(colsBondTable, effectiveColumnWidths);
    return metrics ? metrics.leftBeforeCusip : 0;
  };

  const [animationState, setAnimationState] = useState("entering");
  const [linePosition, setLinePosition] = useState({
    left: branchMidpoint,
    connector: branchMidpoint,
  });
  const [tablePosition, setTablePosition] = useState(() => ({
    left: resolveInitialTableLeft(),
  }));
  const [connectorWidth, setConnectorWidth] = useState(`${branchWidth}px`);
  const [subTableLeftOffset, setSubTableLeftOffset] = useState(0);
  const tableContainer = useRef(null);

  // Recalculate positions when column widths / order change
  useEffect(() => {
    const updatePosition = () => {
      try {
        const metrics = computeCusipMetrics(colsBondTable, effectiveColumnWidths);
        if (!metrics) return;

        const tableLeft = metrics.leftBeforeCusip;
        setTablePosition({ left: tableLeft });

        const cusipCenter = metrics.leftBeforeCusip + metrics.cusipWidth / 2;
        const lineLeftRaw = cusipCenter - tableLeft;
        const bw = metrics.cusipWidth;
        const boundedLineLeft = Math.max(0, Math.min(lineLeftRaw, bw));

        setLinePosition({
          left: `${boundedLineLeft}px`,
          connector: `${boundedLineLeft}px`,
        });

        const connDist = bw - boundedLineLeft;
        setConnectorWidth(`${Math.max(connDist, 0)}px`);

        // Compute how far the subtable needs to shift so bidDealer aligns
        // Offset = bidDealer absolute position - cusip absolute position - cusip width
        // This places the subtable right after the branch visualization
        const bidDealerOffset = computeSubTableLeftOffset(colsBondTable, effectiveColumnWidths);
        setSubTableLeftOffset(bidDealerOffset - tableLeft - metrics.cusipWidth);
      } catch (error) {
        console.error("MABranchTable: Error calculating positions", error);
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [colsBondTable, effectiveColumnWidths]);

  // Start animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationState("entered");
    }, INITIAL_ANIMATION_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      ref={tableContainer}
      sx={{
        display: "flex",
        flexDirection: "row",
        mt: 0,
        mb: 0,
        ml: `${tablePosition.left}px`,
        opacity: animationState === "entered" ? 1 : 0,
        transform:
          animationState === "entered"
            ? "translateY(0)"
            : `translateY(${TRANSLATION_Y_OFFSET})`,
        transition: "opacity 0.3s ease-in-out, transform 0.3s ease-in-out",
        overflow: "hidden",
        willChange: "opacity, transform",
      }}
    >
      {/* Branch Visualization (L-shape lines) */}
      <MABranchVisualization
        rows={dealers}
        animationState={animationState}
        linePosition={linePosition}
        connectorWidth={connectorWidth}
        branchWidth={branchWidth}
      />

      {/* Subtable — positioned to align under main table columns */}
      <Box sx={{ ml: `${Math.max(subTableLeftOffset, 0)}px`, mt: 0 }}>
        <MABranchSubTable
          dealers={dealers}
          animationState={animationState}
          columnWidths={effectiveColumnWidths}
          parentColumns={colsBondTable}
        />
      </Box>
    </Box>
  );
};

export default MABranchTable;
